/**
 * Complete the Look — PDP Companion Auto-Add (v2)
 *
 * Datenquelle: product.metafields.lora.suggested_bundle.value (Bundle-Metaobject).
 * Wenn der User mindestens eine Companion-Checkbox aktiviert:
 *  1. JS hängt der buy-buttons-Form on-the-fly Hidden-Inputs
 *     `properties[_bundle_handle]` + `properties[_bundle_slot_id]` an,
 *     damit der Anker beim Add die Bundle-Properties trägt und die v2-Function
 *     den Bundle-Rabatt + Per-Option-Override appliziert.
 *  2. Nach dem Anker-Add (cart:update) wird ein Follow-up `/cart/add.js` für
 *     jede gecheckte Companion-Row gefired, ebenfalls mit Bundle-Properties.
 *  3. Box uncheck → Hidden-Inputs werden entfernt, Anker geht regulär in den Cart,
 *     der Drawer-Upsell (lora-bundles-index) triggert nachgelagert.
 *
 * Inline-Dropdown pro Row (wenn slot.options.length > 1): Select-Change updated
 * data-variant-id, data-quantity, data-effective-price, Thumbnail, Title und
 * Preis-Anzeige der Row.
 */

const SOURCE_TAG = 'complete-the-look-companion';
const HIDDEN_INPUT_FLAG = 'data-lora-bundle-property';

class LoraCompleteLook extends HTMLElement {
  connectedCallback() {
    this._toggles = Array.from(this.querySelectorAll('[data-companion-toggle]'));
    this._rows = Array.from(this.querySelectorAll('[data-companion-row]'));
    this._selects = Array.from(this.querySelectorAll('[data-companion-select]'));
    this._detailLinks = Array.from(this.querySelectorAll('[data-companion-detail-link]'));
    this._companionLightboxTriggers = Array.from(this.querySelectorAll('[data-companion-lightbox-trigger]'));
    this._companionLightbox = this.querySelector('[data-companion-lightbox]');
    this._companionLightboxImg = this.querySelector('[data-companion-lightbox-image]');
    this._companionLightboxClose = this.querySelector('[data-companion-lightbox-close]');
    this._footnoteEl = this.querySelector('[data-footnote]');
    this._lightboxTrigger = this.querySelector('[data-lightbox-trigger]');
    this._lightbox = this.querySelector('[data-lightbox]');
    this._lightboxCloseBtn = this.querySelector('[data-lightbox-close]');

    if (this._toggles.length === 0) return;

    this._anchorVariantId = this.dataset.anchorVariantId;
    this._anchorProductId = this.dataset.anchorProductId;
    this._bundleHandle = this.dataset.bundleHandle || '';
    this._anchorSlotId = this.dataset.anchorSlotId || '';

    this._onVariantUpdate = this._onVariantUpdate.bind(this);
    this._onCartUpdate = this._onCartUpdate.bind(this);
    this._onToggle = this._onToggle.bind(this);
    this._onSelectChange = this._onSelectChange.bind(this);
    this._onLightboxOpen = this._onLightboxOpen.bind(this);
    this._onLightboxClose = this._onLightboxClose.bind(this);
    this._onLightboxBackdrop = this._onLightboxBackdrop.bind(this);
    this._onCompanionLightboxOpen = this._onCompanionLightboxOpen.bind(this);
    this._onCompanionLightboxClose = this._onCompanionLightboxClose.bind(this);
    this._onCompanionLightboxBackdrop = this._onCompanionLightboxBackdrop.bind(this);

    document.addEventListener('variant:update', this._onVariantUpdate);
    document.addEventListener('cart:update', this._onCartUpdate);
    this._toggles.forEach((cb) => cb.addEventListener('change', this._onToggle));
    this._selects.forEach((sel) => sel.addEventListener('change', this._onSelectChange));
    this._detailLinks.forEach((link) => link.addEventListener('click', (e) => e.stopPropagation()));
    this._companionLightboxTriggers.forEach((trigger) => trigger.addEventListener('click', this._onCompanionLightboxOpen));
    if (this._companionLightbox) {
      this._companionLightboxClose?.addEventListener('click', this._onCompanionLightboxClose);
      this._companionLightbox.addEventListener('click', this._onCompanionLightboxBackdrop);
    }

    if (this._lightboxTrigger && this._lightbox) {
      this._lightboxTrigger.addEventListener('click', this._onLightboxOpen);
      this._lightboxCloseBtn?.addEventListener('click', this._onLightboxClose);
      this._lightbox.addEventListener('click', this._onLightboxBackdrop);
    }

    this._dispatchExtra();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this._onVariantUpdate);
    document.removeEventListener('cart:update', this._onCartUpdate);
    this._toggles?.forEach((cb) => cb.removeEventListener('change', this._onToggle));
    this._selects?.forEach((sel) => sel.removeEventListener('change', this._onSelectChange));
    this._lightboxTrigger?.removeEventListener('click', this._onLightboxOpen);
    this._lightboxCloseBtn?.removeEventListener('click', this._onLightboxClose);
    this._lightbox?.removeEventListener('click', this._onLightboxBackdrop);
    this._companionLightboxTriggers?.forEach((t) => t.removeEventListener('click', this._onCompanionLightboxOpen));
    this._companionLightboxClose?.removeEventListener('click', this._onCompanionLightboxClose);
    this._companionLightbox?.removeEventListener('click', this._onCompanionLightboxBackdrop);
    // Stale Hidden-Inputs aus der Anker-Form entfernen
    const form = this._findAnchorForm();
    form?.querySelectorAll(`[${HIDDEN_INPUT_FLAG}]`).forEach((el) => el.remove());
  }

  _onCompanionLightboxOpen(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this._companionLightbox || !this._companionLightboxImg) return;
    const trigger = event.currentTarget;
    const url = trigger.dataset.imgUrl;
    const alt = trigger.dataset.imgAlt || '';
    if (!url) return;
    this._companionLightboxImg.src = url;
    this._companionLightboxImg.alt = alt;
    if (typeof this._companionLightbox.showModal === 'function') {
      this._companionLightbox.showModal();
    } else {
      this._companionLightbox.setAttribute('open', '');
    }
  }

  _onCompanionLightboxClose() {
    if (!this._companionLightbox) return;
    if (typeof this._companionLightbox.close === 'function') {
      this._companionLightbox.close();
    } else {
      this._companionLightbox.removeAttribute('open');
    }
  }

  _onCompanionLightboxBackdrop(event) {
    if (event.target === this._companionLightbox) this._onCompanionLightboxClose();
  }

  _onToggle() {
    this._syncAnchorBundleHiddenInputs();
    this._dispatchExtra();
  }

  _onSelectChange(event) {
    const select = event.target;
    const row = select.closest('[data-companion-row]');
    if (!row) return;
    const opt = select.options[select.selectedIndex];
    if (!opt) return;

    row.dataset.variantId = opt.value;
    if (opt.dataset.productId) row.dataset.productId = opt.dataset.productId;
    if (opt.dataset.quantity) row.dataset.quantity = opt.dataset.quantity;
    if (opt.dataset.fullPrice) row.dataset.fullPrice = opt.dataset.fullPrice;
    if (opt.dataset.effectivePrice) row.dataset.effectivePrice = opt.dataset.effectivePrice;

    const thumb = row.querySelector('[data-companion-thumb]');
    if (thumb && opt.dataset.thumbUrl) {
      thumb.src = opt.dataset.thumbUrl;
      thumb.alt = opt.dataset.thumbAlt || opt.dataset.productTitle || '';
    }

    const thumbBtn = row.querySelector('[data-companion-lightbox-trigger]');
    if (thumbBtn) {
      if (opt.dataset.thumbLargeUrl) thumbBtn.dataset.imgUrl = opt.dataset.thumbLargeUrl;
      if (opt.dataset.thumbAlt) thumbBtn.dataset.imgAlt = opt.dataset.thumbAlt;
    }

    const detailLink = row.querySelector('[data-companion-detail-link]');
    if (detailLink && opt.dataset.productUrl) {
      detailLink.href = opt.dataset.productUrl;
    }

    const nameEl = row.querySelector('[data-companion-name]');
    if (nameEl && opt.dataset.productTitle) {
      nameEl.textContent = opt.dataset.productTitle;
    }

    const full = parseInt(row.dataset.fullPrice, 10) || 0;
    const eff = parseInt(row.dataset.effectivePrice, 10) || full;
    const wasEl = row.querySelector('[data-companion-was]');
    const nowEl = row.querySelector('[data-companion-price]');
    if (nowEl) nowEl.textContent = `+${this._formatMoney(eff)}`;
    if (wasEl) {
      if (eff < full) {
        wasEl.textContent = `+${this._formatMoney(full)}`;
        wasEl.hidden = false;
      } else {
        wasEl.hidden = true;
      }
    }

    this._dispatchExtra();
  }

  _formatMoney(cents) {
    // Theme-Helper falls verfügbar
    const moneyFormat = window.Theme?.moneyFormat || window.Shopify?.money_format || '{{amount}} €';
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  _checkedRows() {
    return this._toggles
      .filter((cb) => cb.checked && !cb.disabled)
      .map((cb) => cb.closest('[data-companion-row]'))
      .filter(Boolean);
  }

  _findAnchorForm() {
    const formComponent = this.closest('product-form-component')
      || document.querySelector(`product-form-component[data-product-id="${this._anchorProductId}"]`);
    return formComponent?.querySelector('form[data-type="add-to-cart-form"]') || null;
  }

  _syncAnchorBundleHiddenInputs() {
    const form = this._findAnchorForm();
    if (!form) return;
    const anyChecked = this._checkedRows().length > 0;
    const existing = form.querySelectorAll(`[${HIDDEN_INPUT_FLAG}]`);

    if (!anyChecked || !this._bundleHandle || !this._anchorSlotId) {
      existing.forEach((el) => el.remove());
      return;
    }
    if (existing.length > 0) return; // schon gesetzt

    const handleInput = document.createElement('input');
    handleInput.type = 'hidden';
    handleInput.name = 'properties[_bundle_handle]';
    handleInput.value = this._bundleHandle;
    handleInput.setAttribute(HIDDEN_INPUT_FLAG, '');

    const slotInput = document.createElement('input');
    slotInput.type = 'hidden';
    slotInput.name = 'properties[_bundle_slot_id]';
    slotInput.value = this._anchorSlotId;
    slotInput.setAttribute(HIDDEN_INPUT_FLAG, '');

    const sourceInput = document.createElement('input');
    sourceInput.type = 'hidden';
    sourceInput.name = 'properties[_bundle_source]';
    sourceInput.value = 'lora-pdp-complete-look';
    sourceInput.setAttribute(HIDDEN_INPUT_FLAG, '');

    form.appendChild(handleInput);
    form.appendChild(slotInput);
    form.appendChild(sourceInput);
  }

  _dispatchExtra() {
    let extra = 0;
    this._checkedRows().forEach((row) => {
      extra += parseInt(row.dataset.effectivePrice, 10) || parseInt(row.dataset.fullPrice, 10) || 0;
    });
    window.__loraBundleExtra = extra;
    document.dispatchEvent(
      new CustomEvent('lora-bundle:extra', { bubbles: true, detail: { extra } }),
    );
  }

  _onLightboxOpen() {
    if (typeof this._lightbox.showModal === 'function') {
      this._lightbox.showModal();
    } else {
      this._lightbox.setAttribute('open', '');
    }
  }

  _onLightboxClose() {
    if (typeof this._lightbox.close === 'function') {
      this._lightbox.close();
    } else {
      this._lightbox.removeAttribute('open');
    }
  }

  _onLightboxBackdrop(event) {
    if (event.target === this._lightbox) this._onLightboxClose();
  }

  _onVariantUpdate(event) {
    const variantId = event.detail?.resource?.id;
    if (variantId) this._anchorVariantId = String(variantId);
  }

  _isAnchorAddEvent(event) {
    const data = event.detail?.data;
    if (!data) return false;
    if (data.source === SOURCE_TAG) return false;
    if (data.source === 'lora-pdp-complete-look-companion') return false;
    if (data.productId && String(data.productId) === String(this._anchorProductId)) return true;
    if (data.variantId && String(data.variantId) === String(this._anchorVariantId)) return true;
    return false;
  }

  _collectCheckedCompanions() {
    const items = [];
    const rows = [];
    this._checkedRows().forEach((row) => {
      const variantId = row.dataset.variantId;
      if (!variantId) return;
      const quantity = parseInt(row.dataset.quantity, 10) || 1;
      const slotId = row.dataset.slotId;
      const properties = {
        _bundle_source: 'lora-pdp-complete-look',
      };
      if (this._bundleHandle) properties._bundle_handle = this._bundleHandle;
      if (slotId) properties._bundle_slot_id = slotId;
      items.push({ id: variantId, quantity, properties });
      rows.push({ row, toggle: row.querySelector('[data-companion-toggle]') });
    });
    return { items, rows };
  }

  _collectCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const ids = [];
    cartItemsComponents.forEach((c) => {
      if (c instanceof HTMLElement && c.dataset.sectionId) ids.push(c.dataset.sectionId);
    });
    return ids.join(',');
  }

  async _onCartUpdate(event) {
    if (!this._isAnchorAddEvent(event)) return;

    let { items, rows } = this._collectCheckedCompanions();
    if (items.length === 0) return;

    // Dedup: Companions, die bereits im Warenkorb liegen (egal wie hineingekommen,
    // z.B. als Gratis-Geschenk oder Einzelkauf), NICHT erneut hinzufügen.
    try {
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartRes.json();
      const inCart = new Set((cart.items || []).map((i) => String(i.product_id)));
      const keptItems = [];
      const keptRows = [];
      items.forEach((it, i) => {
        const pid = rows[i] && rows[i].row && rows[i].row.dataset.productId;
        const norm = pid ? String(pid).split('/').pop() : null;
        if (norm && inCart.has(norm)) {
          const toggle = rows[i] && rows[i].toggle;
          if (toggle) {
            toggle.checked = false;
            toggle.disabled = false;
          }
        } else {
          keptItems.push(it);
          keptRows.push(rows[i]);
        }
      });
      items = keptItems;
      rows = keptRows;
    } catch (e) {
      // Konservativ: bei Fehler wie bisher fortfahren (kein harter Block).
    }
    if (items.length === 0) {
      this._syncAnchorBundleHiddenInputs();
      return;
    }

    rows.forEach(({ toggle }) => { if (toggle) toggle.disabled = true; });

    const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const payload = { items, sections: this._collectCartSectionIds() };

    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Companion add failed: ${res.status}`);
        return res.json();
      })
      .then((response) => {
        rows.forEach(({ row, toggle }) => {
          if (toggle) toggle.checked = false;
          row.classList.add('lora-complete-look__row--added');
        });
        // Anker-Hidden-Inputs entfernen — wir haben dem Add ja schon mitgegeben
        this._syncAnchorBundleHiddenInputs();
        if (this._footnoteEl) {
          this._footnoteEl.textContent = 'Look-Vorteil im Warenkorb ✓';
          this._footnoteEl.hidden = false;
        }
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: response,
              sourceId: 'lora-complete-look',
              data: { source: 'lora-pdp-complete-look-companion', sections: response?.sections },
            },
          }),
        );
      })
      .catch((error) => {
        console.error('[lora-complete-look] companion add failed', error);
        rows.forEach(({ toggle }) => { if (toggle) toggle.disabled = false; });
        if (this._footnoteEl) {
          this._footnoteEl.textContent = 'Companion konnte nicht hinzugefügt werden — bitte erneut versuchen.';
          this._footnoteEl.hidden = false;
        }
        document.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { sourceId: 'lora-complete-look', data: { source: SOURCE_TAG, error } },
          }),
        );
      });
  }
}

if (!customElements.get('lora-complete-look')) {
  customElements.define('lora-complete-look', LoraCompleteLook);
}
